import type { ComponentFn } from '@pyreon/core'
import type { LinkConfig } from './typed-routes'

export type { ComponentFn }

// ─── Path param extraction ────────────────────────────────────────────────────

/**
 * Extracts typed params from a path string at compile time.
 * Supports optional params via `:param?` — their type is `string | undefined`.
 *
 * @example
 * ExtractParams<'/user/:id/posts/:postId'>
 * // → { id: string; postId: string }
 *
 * ExtractParams<'/user/:id?'>
 * // → { id?: string | undefined }
 */
export type ExtractParams<T extends string> = T extends `${string}:${infer Param}*/${infer Rest}`
  ? { [K in Param]: string } & ExtractParams<`/${Rest}`>
  : T extends `${string}:${infer Param}*`
    ? { [K in Param]: string }
    : T extends `${string}:${infer Param}?/${infer Rest}`
      ? { [K in Param]?: string | undefined } & ExtractParams<`/${Rest}`>
      : T extends `${string}:${infer Param}?`
        ? { [K in Param]?: string | undefined }
        : T extends `${string}:${infer Param}/${infer Rest}`
          ? { [K in Param]: string } & ExtractParams<`/${Rest}`>
          : T extends `${string}:${infer Param}`
            ? { [K in Param]: string }
            : Record<never, never>

// ─── Route meta ───────────────────────────────────────────────────────────────

/**
 * Route metadata interface. Extend it via module augmentation to add custom fields:
 *
 * @example
 * // globals.d.ts
 * declare module "@pyreon/router" {
 *   interface RouteMeta {
 *     requiresRole?: "admin" | "user"
 *     pageTitle?: string
 *   }
 * }
 */
export interface RouteMeta {
  /** Sets document.title on navigation */
  title?: string
  /** Page description (for meta tags) */
  description?: string
  /** If true, guards can redirect to login */
  requiresAuth?: boolean
  /** Scroll behavior for this route */
  scrollBehavior?: 'top' | 'restore' | 'none'
  /** Set to false to disable View Transitions API for this route. Default: true */
  viewTransition?: boolean
}

// ─── Resolved route ───────────────────────────────────────────────────────────

export interface ResolvedRoute<
  P extends Record<string, string | undefined> = Record<string, string>,
  Q extends Record<string, string> = Record<string, string>,
> {
  path: string
  params: P
  query: Q
  hash: string
  /** All matched records from root to leaf (one per nesting level) */
  matched: RouteRecord[]
  /**
   * Pre-merged route metadata for the resolved chain.
   *
   * **Frozen — do not mutate.** This object is shared across every
   * navigation that resolves through the same FlattenedRoute (in
   * particular, dynamic routes like `/posts/[id]` see the SAME meta
   * object identity for /posts/42 and /posts/99 — that's the cache that
   * keeps `resolveRoute` O(1)). Mutation would silently pollute the
   * cache for every future navigation; `Object.freeze` makes such
   * mutation throw in strict mode (every module file is strict by
   * default). To carry per-navigation state, attach it to your own
   * store / context — never write through `route.meta`.
   */
  meta: Readonly<RouteMeta>
  /**
   * Validated search params — populated when the matched route has `validateSearch`.
   * Contains the typed result of `validateSearch(query)`. Use `useValidatedSearch()`
   * to access this in components with full type inference.
   * Empty object `{}` when no `validateSearch` is configured.
   */
  search?: Record<string, unknown> | undefined
  /** Middleware data attached during navigation (populated by middleware chain) */
  _middlewareData?: Record<string, unknown> | undefined
  /**
   * `true` when the URL didn't match any route AND a parent record's
   * `notFoundComponent` was used as a synthetic fallback leaf. The
   * `matched` chain ends with a synthetic `RouteRecord` rendering the
   * not-found component INSIDE all its ancestor layouts — so 404 pages
   * carry the same chrome (headers, footers, navigation) as regular
   * pages. SSR handlers read this to set HTTP status 404.
   */
  isNotFound?: boolean
}

// ─── Lazy component ───────────────────────────────────────────────────────────

export const LAZY_SYMBOL = Symbol('pyreon.lazy')

export interface LazyComponent {
  readonly [LAZY_SYMBOL]: true
  readonly loader: () => Promise<ComponentFn | { default: ComponentFn }>
  /** Optional component shown while the lazy chunk is loading */
  readonly loadingComponent?: ComponentFn
  /** Optional component shown after all retries have failed */
  readonly errorComponent?: ComponentFn
  /**
   * Dev-only module id, emitted by `@pyreon/zero`'s fs-router codegen as
   * `lazy(() => import("/abs/X"), { hmrId: "/abs/X" })`. The HMR coordinator
   * keys the active route's matched records by this id so a hot-updated
   * module can be swapped IN PLACE (no page reload) using the fresh module
   * Vite hands the `import.meta.hot.accept` callback — sidestepping the
   * stale-`?t=` problem where re-running the dynamic-import thunk inside a
   * non-invalidated virtual routes module would return the OLD module.
   * Inert in production (no coordinator is registered when not in dev).
   */
  readonly _hmrId?: string
}

export function lazy(
  loader: () => Promise<ComponentFn | { default: ComponentFn }>,
  options?: { loading?: ComponentFn; error?: ComponentFn; hmrId?: string },
): LazyComponent {
  return {
    [LAZY_SYMBOL]: true,
    loader,
    ...(options?.loading ? { loadingComponent: options.loading } : {}),
    ...(options?.error ? { errorComponent: options.error } : {}),
    ...(options?.hmrId ? { _hmrId: options.hmrId } : {}),
  }
}

export function isLazy(c: RouteComponent): c is LazyComponent {
  return typeof c === 'object' && c !== null && (c as LazyComponent)[LAZY_SYMBOL] === true
}

export type RouteComponent = ComponentFn | LazyComponent

// ─── Navigation guard ─────────────────────────────────────────────────────────

export type NavigationGuardResult = boolean | string | undefined
export type NavigationGuard = (
  to: ResolvedRoute,
  from: ResolvedRoute,
) => NavigationGuardResult | Promise<NavigationGuardResult>

/**
 * What a navigation ultimately did — the resolved value of
 * `router.push()` / `router.replace()`:
 *   - `'committed'`  — the target route (or a redirect it chained into) is
 *     now the current route;
 *   - `'cancelled'`  — a blocker / guard / middleware / the redirect-depth
 *     cap refused it; the current route is unchanged;
 *   - `'superseded'` — a newer navigation started while this one was in
 *     flight and won; this one never committed.
 *
 * Mirrors Vue Router's navigation-failure detection in a value-first shape:
 * `if (await router.push('/checkout') !== 'committed') { … }`. Callers that
 * ignore the value keep working (`await router.push(...)` — the promise
 * still settles exactly when the navigation settles).
 */
export type NavigationResult = 'committed' | 'cancelled' | 'superseded'

export type AfterEachHook = (to: ResolvedRoute, from: ResolvedRoute) => void

// ─── Route middleware ────────────────────────────────────────────────────────

/**
 * Context object passed through the middleware chain.
 * Middleware can read/write arbitrary data on `ctx.data`.
 */
export interface RouteMiddlewareContext {
  /** The route being navigated to. */
  to: ResolvedRoute
  /** The route being navigated from. */
  from: ResolvedRoute
  /** Shared data — middleware can accumulate state here for downstream middleware/components. */
  data: Record<string, unknown>
}

/**
 * Route middleware function. Called before guards.
 * - Return nothing/undefined to continue
 * - Return `false` to cancel navigation
 * - Return a string to redirect
 */
export type RouteMiddleware = (
  ctx: RouteMiddlewareContext,
) => void | false | string | Promise<void | false | string>

// ─── Navigation blockers ──────────────────────────────────────────────────────

/**
 * Called before each navigation. Return `true` to block, `false` to allow.
 * Async blockers are supported (e.g. to show a confirmation dialog).
 */
export type BlockerFn = (to: ResolvedRoute, from: ResolvedRoute) => boolean | Promise<boolean>

export interface Blocker {
  /** Unregister this blocker so future navigations proceed freely. */
  remove(): void
}

// ─── Route loaders ────────────────────────────────────────────────────────────

export interface LoaderContext {
  params: Record<string, string>
  query: Record<string, string>
  /** Aborted when a newer navigation supersedes this one */
  signal: AbortSignal
  /**
   * The incoming HTTP `Request` — populated only when the loader runs during
   * SSR (via `prefetchLoaderData`); `undefined` on every CSR navigation.
   * Lets server-side loaders read cookies / auth headers and decide whether
   * to `throw redirect('/login')` BEFORE the layout renders.
   *
   * @example
   * loader: ({ request }) => {
   *   const cookie = request?.headers.get('cookie') ?? ''
   *   const sid = cookie.match(/sid=([^;]+)/)?.[1]
   *   if (!sid) redirect('/login')
   *   return { sid }
   * }
   */
  request?: Request
}

export type RouteLoaderFn = (ctx: LoaderContext) => Promise<unknown>

/**
 * Derive a loader's RESOLVED data type from the loader function itself —
 * "derive, don't annotate twice". Works for async loaders (unwraps the
 * Promise via `Awaited`) and sync-returning ones alike.
 *
 * Type-only, zero runtime bytes. Pair it with `useLoaderData`:
 *
 * @example
 * ```ts
 * // routes/posts.tsx
 * export const loader = async () => ({ posts: await fetchPosts() })
 *
 * function PostsPage() {
 *   const data = useLoaderData<LoaderData<typeof loader>>()
 *   // data: { posts: Post[] } — follows the loader, no second annotation
 * }
 * ```
 */
export type LoaderData<L> = L extends (...args: never[]) => infer R ? Awaited<R> : never

// ─── Route record ─────────────────────────────────────────────────────────────

export interface RouteRecord<TPath extends string = string> {
  /** Path pattern — supports `:param` segments and `(.*)` wildcard */
  path: TPath
  component: RouteComponent
  /** Optional route name for named navigation */
  name?: string
  /** Metadata attached to this route */
  meta?: RouteMeta
  /**
   * Redirect target. Evaluated before guards.
   * String: redirect to that path.
   * Function: called with the resolved route, return path string.
   */
  redirect?: string | ((to: ResolvedRoute) => string)
  /** Guard(s) run only for this route, before global beforeEach guards */
  beforeEnter?: NavigationGuard | NavigationGuard[]
  /** Guard(s) run before leaving this route. Return false to cancel. */
  beforeLeave?: NavigationGuard | NavigationGuard[]
  /**
   * Alternative path(s) for this route. Alias paths render the same component
   * and share guards, loaders, and metadata with the primary path.
   *
   * @example
   * { path: "/user/:id", alias: ["/profile/:id"], component: UserPage }
   */
  alias?: string | string[]
  /** Child routes rendered inside this route's component via <RouterView /> */
  children?: RouteRecord[]
  /**
   * Data loader — runs before navigation commits, in parallel with sibling loaders.
   * The result is accessible via `useLoaderData()` inside the route component.
   * Receives an AbortSignal that fires if a newer navigation supersedes this one.
   */
  loader?: RouteLoaderFn
  /**
   * Phase 5 — SERVER loader. Present as a real function ONLY in the
   * server module graph (zero's fs-router emits the `.server.ts` sibling
   * import for SSR builds exclusively); the client graph carries just
   * `hasServerLoader`. On the server it runs exactly like `loader` (full
   * `LoaderContext`, incl. `request` during SSR). A record must not have
   * BOTH — zero's scanner fails the build with the fix spelled out.
   */
  serverLoader?: RouteLoaderFn
  /**
   * Phase 5 — serializable marker that this record has a server loader.
   * On client-side navigations the router fetches the whole matched
   * chain's server-loader data in ONE request from the data endpoint
   * (default `/_pyreon/data`) instead of running anything locally.
   */
  hasServerLoader?: boolean
  /**
   * When true, the router shows cached loader data immediately (stale) and
   * revalidates in the background. The component re-renders once fresh data arrives.
   * Only applies when navigating to a route that already has cached loader data.
   */
  staleWhileRevalidate?: boolean
  /**
   * Cache key function for loader data. Returns a string key derived from
   * route params/query. When the key matches cached data, the loader is
   * skipped (cache hit). Default: `path + JSON.stringify(params)`.
   *
   * @example
   * ```ts
   * loaderKey: ({ params }) => `user-${params.id}`
   * ```
   */
  loaderKey?: (ctx: Pick<LoaderContext, 'params' | 'query'>) => string
  /**
   * Time in ms to keep cached loader data before garbage collection.
   * Default: 300000 (5 minutes). Set to 0 to disable caching.
   * Stale data is still served immediately if `staleWhileRevalidate` is true.
   */
  gcTime?: number
  /** Component rendered when this route's loader throws an error */
  errorComponent?: ComponentFn
  /**
   * Component rendered when a URL doesn't match any descendant route under
   * this record's path. Acts as a "404 within layout" — the matched chain
   * is `[...ancestors, this, syntheticLeaf]` so the not-found component
   * renders INSIDE this layout's chrome. fs-router attaches this when it
   * detects a `_404.tsx` / `_not-found.tsx` file under this layout.
   */
  notFoundComponent?: ComponentFn
  /**
   * Component rendered while this route's loader is running.
   * Only shown after `pendingMs` (default: 0) to avoid flash on fast loads.
   * Once shown, displayed for at least `pendingMinMs` (default: 200) to avoid flicker.
   */
  pendingComponent?: ComponentFn
  /** Delay in ms before showing pendingComponent (default: 0). Prevents flash on fast loaders. */
  pendingMs?: number
  /** Minimum display time in ms for pendingComponent once shown (default: 200). Prevents flicker. */
  pendingMinMs?: number
  /**
   * Validate and transform raw query string parameters into typed values.
   * Receives the raw `Record<string, string>` from the URL and returns
   * a typed object. The validated result is available via `useValidatedSearch()`.
   *
   * Accepts any function — use Zod `.parse`, Valibot, or a plain function:
   *
   * @example
   * ```ts
   * // Plain function:
   * validateSearch: (raw) => ({
   *   page: Number(raw.page) || 1,
   *   q: raw.q ?? '',
   * })
   *
   * // With Zod:
   * validateSearch: z.object({
   *   page: z.coerce.number().default(1),
   *   q: z.string().default(''),
   * }).parse
   * ```
   */
  validateSearch?: (raw: Record<string, string>) => Record<string, unknown>
  /** Per-route middleware — runs before guards, can accumulate context data. */
  middleware?: RouteMiddleware | RouteMiddleware[]
}

// ─── Router options ───────────────────────────────────────────────────────────

export type ScrollBehaviorFn = (
  to: ResolvedRoute,
  from: ResolvedRoute,
  savedPosition: number | null,
) => 'top' | 'restore' | 'none' | number

export interface RouterOptions {
  routes: RouteRecord[]
  /** "hash" (default) uses location.hash; "history" uses pushState */
  mode?: 'hash' | 'history'
  /**
   * Phase 5 — endpoint the client-side router fetches server-loader data
   * from on navigations (one request per navigation for the whole matched
   * chain). Default `"/_pyreon/data"` — zero's `createServer` auto-mounts
   * it; prefix with your `base` for sub-path deploys.
   */
  dataEndpoint?: string
  /**
   * Base path for the application. Used when deploying to a sub-path
   * (e.g. `"/app"` for `https://example.com/app/`).
   * Only applies in history mode. Must start with `/`.
   * Default: `""` (no base path).
   */
  base?: string
  /**
   * Global scroll behavior. Per-route meta.scrollBehavior takes precedence.
   * Default: "top"
   */
  scrollBehavior?: ScrollBehaviorFn | 'top' | 'restore' | 'none'
  /**
   * Initial URL for SSR. On the server, window.location is unavailable;
   * pass the request URL here so the router resolves the correct route.
   *
   * @example
   * // In your SSR handler:
   * const router = createRouter({ routes, url: req.url })
   */
  url?: string
  /**
   * Called when a route loader throws. If not provided, errors are logged
   * and the navigation continues with `undefined` data for the failed loader.
   * Return `false` to cancel the navigation.
   */
  onError?: (err: unknown, route: ResolvedRoute) => undefined | false
  /**
   * Maximum number of resolved lazy components to cache.
   * When exceeded, the oldest entry is evicted.
   * Default: 100.
   */
  maxCacheSize?: number
  /**
   * Trailing slash handling:
   *   - `"strip"` — removes trailing slashes before matching (default)
   *   - `"add"` — ensures paths always end with `/`
   *   - `"ignore"` — no normalization
   */
  trailingSlash?: 'strip' | 'add' | 'ignore'
  /**
   * `<RouterLink>` external-link behaviour (same-origin-absolute policy,
   * new-tab, and `rel`). See {@link LinkConfig}.
   */
  links?: LinkConfig
}

// ─── Router interface ─────────────────────────────────────────────────────────

/**
 * Router interface. Parameterized by route name union for type-safe named navigation.
 *
 * @example
 * ```ts
 * type MyRoutes = 'home' | 'user' | 'settings'
 * const router: Router<MyRoutes> = createRouter({ routes })
 * router.push({ name: 'user', params: { id: '42' } }) // ✓
 * router.push({ name: 'typo' })                        // TS error
 * ```
 */
export interface Router<TNames extends string = string> {
  /**
   * Which part of the URL this router treats as the route — `'hash'` (the
   * DEFAULT: the whole route, query included, lives in the fragment, and
   * `location.search` is empty) or `'history'`.
   *
   * Public because a consumer that writes URLs alongside the router has to ask:
   * `@pyreon/url-state` built `/{pathname}?{search}` for every router and so
   * navigated a hash-mode app off its own route on the first param write. The
   * field was on the router object from the start — only the public TYPE
   * omitted it, which is what let the wrong assumption look correct.
   */
  readonly mode: 'hash' | 'history'
  /** Navigate to a path. Resolves with what the navigation did — see {@link NavigationResult}. */
  push(path: string): Promise<NavigationResult>
  /** Navigate to a named route */
  push(location: {
    name: TNames
    params?: Record<string, string>
    query?: Record<string, string>
  }): Promise<NavigationResult>
  /** Replace current history entry. Resolves with what the navigation did — see {@link NavigationResult}. */
  replace(path: string): Promise<NavigationResult>
  /** Replace current history entry using a named route */
  replace(location: {
    name: TNames
    params?: Record<string, string>
    query?: Record<string, string>
  }): Promise<NavigationResult>
  /** Go back one step in history */
  back(): void
  /** Go forward one step in history */
  forward(): void
  /** Navigate forward or backward by `delta` steps in the history stack */
  go(delta: number): void
  /** Register a global before-navigation guard. Returns an unregister function. */
  beforeEach(guard: NavigationGuard): () => void
  /** Register a global after-navigation hook. Returns an unregister function. */
  afterEach(hook: AfterEachHook): () => void
  /** Current resolved route (reactive signal) */
  readonly currentRoute: () => ResolvedRoute
  /** True while a navigation (guards + loaders) is in flight */
  readonly loading: () => boolean
  /**
   * Promise that resolves once the initial navigation is complete.
   * Useful for SSR and for delaying rendering until the first route is resolved.
   */
  isReady(): Promise<void>
  /**
   * Resolve `path` and prepare everything needed to render it: load any lazy
   * route components into the router's cache and run the matched routes'
   * loaders. After this resolves, a `RouterView` rendered against this router
   * for `path` will produce final HTML synchronously — no loading fallbacks,
   * no `useLoaderData()` returning `undefined`.
   *
   * Used by SSR/SSG to hydrate the route tree before `renderToString`.
   * The router's `currentRoute` is NOT changed by `preload` — pass the path
   * separately when creating the router (`createRouter({ url, ... })`) or
   * call this for the same `url` you initialised the router with.
   */
  preload(
    path: string,
    request?: Request,
    options?: { skipLoaders?: boolean },
  ): Promise<void>
  /**
   * Phase 5 — run ONLY the matched chain's `serverLoader` records for the
   * single-fetch data endpoint, keyed by matched-chain index. Returns the
   * index-keyed data, or a redirect descriptor when a server loader threw
   * `redirect()`. Server-only (the `serverLoader` fn exists only in the SSR
   * module graph).
   */
  runServerLoaders(
    path: string,
    request?: Request,
  ): Promise<
    | { kind: 'data'; data: Record<number, unknown> }
    | { kind: 'redirect'; to: string; status: number }
  >
  /**
   * Invalidate cached loader data. Forces loaders to re-run on next navigation.
   * - No args: invalidate ALL cached loader data
   * - String: invalidate by cache key (as returned by `loaderKey`)
   * - Function: invalidate entries where the predicate returns true
   *
   * To refresh the CURRENT route's data in place (mutation-then-refresh
   * flows), use {@link revalidate} instead — `invalidateLoader` only takes
   * effect on the next navigation.
   */
  invalidateLoader(keyOrPredicate?: string | ((key: string) => boolean)): void
  /**
   * Re-run the CURRENT route's loaders in place and re-render the affected
   * route components with fresh data — no navigation required. The
   * mutation-then-refresh primitive (`useRevalidator` in React Router,
   * `router.invalidate()` in TanStack):
   *
   * - drops the current chain's cached loader entries, then re-runs its
   *   loaders through the normal pipeline (server-loader records re-fetch
   *   from the data endpoint; `staleWhileRevalidate` records refresh in the
   *   background per their contract);
   * - a loader that throws `redirect()` navigates (replace semantics);
   * - a real navigation starting mid-revalidate supersedes it (the fresh
   *   navigation's loaders win — no torn state).
   *
   * @example
   * await api.deletePost(id)
   * await router.revalidate() // list loader re-runs, view updates in place
   */
  revalidate(): Promise<void>
  /** Remove all event listeners, clear caches, and abort in-flight navigations. */
  destroy(): void
}

// ─── Internal router instance ─────────────────────────────────────────────────

import type { Computed, Signal } from '@pyreon/reactivity'
import type { SizedMap } from '@pyreon/sized-map'

export interface RouterInstance extends Router {
  routes: RouteRecord[]
  /** Normalized base path (e.g. "/app"), empty string if none */
  _base: string
  _currentPath: Signal<string>
  _currentRoute: Computed<ResolvedRoute>
  _componentCache: SizedMap<RouteRecord, ComponentFn>
  _loadingSignal: Signal<number>
  /**
   * PR-S8: dev-only counter — bumped by `_hmrSwap` after a successful
   * component cache swap. `RouterView`'s `depthEntry` computed
   * subscribes to it alongside `_loadingSignal` so a swap forces a
   * re-emit without leaking into the navigation-loading counter
   * (`useTransition()`). `undefined` in production builds where
   * `_hmrSwap` itself is undefined.
   */
  _hmrTick?: Signal<number>
  _resolve(rawPath: string): ResolvedRoute
  _scrollPositions: Map<string, number>
  _scrollBehavior: RouterOptions['scrollBehavior']
  _onError: RouterOptions['onError']
  _linkConfig: LinkConfig | undefined
  _maxCacheSize: number
  /**
   * Current RouterView nesting depth. Incremented by each RouterView as it
   * mounts (in tree order = depth-first), so each view knows which level of
   * `matched[]` to render. Reset to 0 by RouterProvider.
   */
  _viewDepth: number
  /** Route records whose lazy chunk permanently failed (all retries exhausted) */
  _erroredChunks: Set<RouteRecord>
  /** Loader data keyed by route record — populated before each navigation commits */
  _loaderData: Map<RouteRecord, unknown>
  /** AbortController for the in-flight loader batch — aborted when a newer navigation starts */
  _abortController: AbortController | null
  /** Registered navigation blockers */
  _blockers: Set<BlockerFn>
  /** Resolves the isReady() promise after initial navigation completes */
  _readyResolve: (() => void) | null
  /** The isReady() promise instance */
  _readyPromise: Promise<void>
  /** Timestamp when the current navigation started — used for pendingMs timing */
  _navigationStartTime: number
  /**
   * Middleware data of the last COMMITTED navigation. `runMiddleware`
   * accumulates onto the in-flight `to` object, but `currentRoute()` is a
   * computed that re-resolves a FRESH ResolvedRoute from the path — the
   * in-flight object (and anything attached to it) never becomes the
   * current route. `commitNavigation` copies the accumulated data here
   * (before flipping `currentPath`), and `useMiddlewareData()` reads it
   * alongside a reactive `currentRoute()` subscription. Reset to `{}` on
   * navigations whose chain has no middleware (data is per-navigation).
   */
  _committedMiddlewareData: Record<string, unknown>
  /** Key-based loader cache: cacheKey → { data, timestamp } */
  _loaderCache: SizedMap<string, { data: unknown; timestamp: number }>
  /**
   * Run a record's loader through the router's cache + in-flight dedup
   * (cache hit → cached data; same-key in-flight with a live signal →
   * the existing promise; else run + cache). The SAME path navigations
   * take — `prefetchLoaderData` routes through this so a hover/viewport
   * prefetch and the click that follows it share ONE loader run instead
   * of double-fetching (pre-fix prefetch called `record.loader()` raw,
   * bypassing `_loaderCache` and `_loaderInflight` entirely).
   */
  _executeLoader(record: RouteRecord, ctx: LoaderContext): Promise<unknown>
  /**
   * In-flight loader dedup: cacheKey → { promise, signal }.
   * Tracking the signal lets dedup skip an in-flight entry whose signal is
   * already aborted — otherwise nav-2 would inherit nav-1's aborted promise
   * (`router.push` aborts the previous nav's controller before starting the
   * next, so back-to-back nav to the same path could resolve nav-2 against
   * nav-1's aborted fetch).
   */
  _loaderInflight: Map<string, { promise: Promise<unknown>; signal: AbortSignal }>
  /**
   * Dev-only HMR coordinator. Given a hot-updated module's id and the FRESH
   * module namespace Vite handed `import.meta.hot.accept`, swaps the new
   * component into every matched record whose lazy `_hmrId` equals `id`,
   * then bumps `_hmrTick` (PR-S8) so `RouterView` re-renders ONLY that
   * subtree in place — no page reload, so `__pyreon_hmr_registry__`
   * (module-scope signal values) survives and `__hmr_signal` restores them.
   *
   * Using the namespace Vite passed (not a re-run of the lazy thunk)
   * sidesteps the stale-`?t=` trap: the dynamic-import thunk lives in the
   * virtual routes module, which is NOT invalidated when a leaf route
   * self-accepts, so re-importing it would return the OLD module.
   *
   * Returns `true` when at least one matched component was swapped. `false`
   * tells `@pyreon/vite-plugin`'s accept handler the edit was outside the
   * active route tree (a nested non-route component, an unrelated route, a
   * signal-only module) so it falls back to `import.meta.hot.invalidate()`
   * → an automatic full reload (no manual refresh either way).
   *
   * Present only when the router is created in a dev browser context.
   */
  _hmrSwap?: (id: string, mod: unknown) => boolean
}
